// docs/116 CI5: how a throw moves. Everything here is a pure function of `throwN` and the die, so every
// client in the room sees the same tumble, and the sound director can time the clatter to the landings
// without drawing anything.
import { hashInts } from './dice';

/** A die's moments in ms from the throw: leaves the hand, hits the table (three times), comes to rest. */
export interface DieTiming { start: number; hits: [number, number, number]; end: number }

const FLIGHT_MS = 330, BOUNCE1_MS = 220, BOUNCE2_MS = 140, SKID_MS = 170;

export function dieTiming(throwN: number, i: number): DieTiming {
  const h = hashInts(throwN, i, 11);
  const start = i * 55 + (h % 50);
  const f = FLIGHT_MS + ((h >>> 8) % 60);
  const a = start + f, b = a + BOUNCE1_MS + ((h >>> 14) % 40), c = b + BOUNCE2_MS;
  return { start, hits: [a, b, c], end: c + SKID_MS };
}

/** When the last thrown die of a throw comes to rest. */
export function throwEnd(throwN: number, mask: number): number {
  let end = 0;
  for (let i = 0; i < 3; i++) if (mask & (1 << i)) end = Math.max(end, dieTiming(throwN, i).end);
  return end;
}

/** Where die `i` comes to rest, in the felt's own 0..1 box (left to right by index, jittered by the throw). */
export function restSpot(throwN: number, i: number): { u: number; v: number } {
  const h = hashInts(throwN, i, 29);
  const ju = ((h % 1000) / 1000 - 0.5) * 0.07;
  const jv = (((h >>> 10) % 1000) / 1000 - 0.5) * 0.36;
  return { u: 0.34 + 0.16 * i + ju, v: 0.5 + jv };
}

/** The faces a die shows while it tumbles: one per stretch between hits (the last one is the row's face). */
export function tumbleFace(throwN: number, i: number, k: number): number {
  return 1 + (hashInts(throwN, i, k, 5) % 6);
}

export type DiePose = 'flat' | 'tilt' | 'edge';

export interface DieAt {
  /** 0..1 along the way from the hand to the rest spot. */
  p: number;
  /** Height above the table in art pixels. */
  z: number;
  pose: DiePose;
  face: number;
  /** In flight or still skidding. */
  moving: boolean;
}

const arc = (u: number) => 4 * u * (1 - u);

/** Die `i` of throw `throwN`, `ms` after the throw, settling on `face`. */
export function dieAt(throwN: number, i: number, ms: number, face: number): DieAt {
  const t = dieTiming(throwN, i);
  const [a, b, c] = t.hits;
  const spin = Math.floor(ms / 65) + i;
  const poses: DiePose[] = ['tilt', 'edge', 'flat', 'edge'];
  if (ms <= t.start) return { p: 0, z: 18, pose: 'tilt', face: tumbleFace(throwN, i, 0), moving: true };
  if (ms < a) {
    const u = (ms - t.start) / (a - t.start);
    // out of the hand: up a little, then down to the first hit
    return { p: 0.58 * u, z: 18 * (1 - u) + 14 * arc(u), pose: poses[spin % 4]!, face: tumbleFace(throwN, i, 0), moving: true };
  }
  if (ms < b) {
    const u = (ms - a) / (b - a);
    return { p: 0.58 + 0.24 * u, z: 9 * arc(u), pose: poses[spin % 4]!, face: tumbleFace(throwN, i, 1), moving: true };
  }
  if (ms < c) {
    const u = (ms - b) / (c - b);
    return { p: 0.82 + 0.11 * u, z: 3 * arc(u), pose: u < 0.5 ? 'tilt' : 'flat', face: tumbleFace(throwN, i, 2), moving: true };
  }
  if (ms < t.end) {
    const u = (ms - c) / (t.end - c);
    return { p: 0.93 + 0.07 * (1 - (1 - u) * (1 - u)), z: 0, pose: 'flat', face, moving: true };
  }
  return { p: 1, z: 0, pose: 'flat', face, moving: false };
}
